import type * as React from "react";

import { cn } from "../lib/cn";

/*
 * The two carbons under the top copy: each highlighter lightened over card
 * stock, so they are real opaque paper rather than a wash - the same mix
 * MessageSlip uses for its pink slip. Only a few pixels of each ever show.
 */
const CANARY_PAPER =
  "bg-[color-mix(in_srgb,var(--color-wash-flag)_40%,var(--color-card))]";
const PINK_PAPER =
  "bg-[color-mix(in_srgb,var(--color-wash-stopped)_40%,var(--color-card))]";

/*
 * Edge treatment shared by all three copies in the stack. The lift is kept
 * separate: it varies by copy, and a second shadow class in the same string
 * would merge the first one away.
 */
const SHEET = "rounded-paper border border-rule";

export interface CarbonFormProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Hold the stack further off the page. A host that is itself a reading
   * surface leaves this off (paper resting on paper); a bare list page turns it
   * on.
   */
  lifted?: boolean;
  children: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The carbon triplicate: white, canary, pink - the form clinics already hand
 * across the counter. The top copy holds the whole form and the two carbons
 * peek out beneath it; they are paper, not content, so they are hidden from AT
 * and never catch a pointer.
 *
 * The peek is bought with the stack's own bottom padding and the carbons are
 * inset horizontally, so the stack adds no overflow in either axis: a host with
 * `overflow-y-auto` would otherwise grow a horizontal scrollbar for a few
 * decorative pixels.
 *
 * The carbons are dropped from PDF export (`data-html2canvas-ignore`, honoured
 * by html2canvas-pro when it rasterises this DOM): an exported form is a print
 * of the top copy, so the carbons would only pad the capture with paper edges.
 *
 * Compose the top copy from `FormRow` and `FormTotal` plus whatever head strip
 * and controls the form needs. Sections own their own padding; the form owns
 * only the paper. Native props and `className` land on the stack, which is what
 * a caller positions and sizes.
 */
export function CarbonForm({
  lifted = false,
  className,
  children,
  ...rest
}: CarbonFormProps) {
  return (
    <div className={cn("relative pb-2", className)} {...rest}>
      {/* Furthest copy first: positioned siblings paint in DOM order, so the
          opaque top copy covers all but each carbon's offset bottom edge. */}
      <span
        aria-hidden="true"
        data-html2canvas-ignore="true"
        className={cn(
          "pointer-events-none absolute inset-x-[6px] top-0 bottom-0 shadow-lift",
          SHEET,
          PINK_PAPER,
        )}
      />
      <span
        aria-hidden="true"
        data-html2canvas-ignore="true"
        className={cn(
          "pointer-events-none absolute inset-x-[3px] top-0 bottom-[4px] shadow-lift",
          SHEET,
          CANARY_PAPER,
        )}
      />
      <div
        className={cn(
          "relative bg-card",
          SHEET,
          lifted ? "shadow-lift-lg" : "shadow-lift",
        )}
      >
        {children}
      </div>
    </div>
  );
}

export interface FormRowProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * `li` by default - form rows are line items, and a run of them belongs in a
   * `ul` that names the list. Use `div` for a one-off labelled row that is not
   * part of a list.
   */
  as?: "li" | "div";
  /** What the line is for. Truncates; a string label becomes its own tooltip. */
  label: React.ReactNode;
  /** The figure, set in mono with tabular figures so a column of them lines up. */
  value?: React.ReactNode;
  /** Trailing controls, after the figure (a remove button on an open form). */
  children?: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

/**
 * One ruled line on the form: what it is on the left, what it costs on the
 * right, anything you can do about it after that. The hairline under the row is
 * dropped on the last one, so a run of rows rules off cleanly against whatever
 * follows.
 *
 * The row is scaffolding only - it decides the ruling, the gutters and the two
 * type treatments, and takes no view on what a line item is. Formatting
 * (currency, quantities, "price TBD") stays with the caller.
 */
export function FormRow({
  as = "li",
  label,
  value,
  className,
  children,
  ...rest
}: FormRowProps) {
  const Row = as as React.ElementType;

  return (
    <Row
      className={cn(
        "flex items-center gap-3 border-b border-rule px-4 py-2 last:border-b-0",
        className,
      )}
      {...rest}
    >
      <span
        className="min-w-0 flex-1 truncate font-body text-sm text-ink-body"
        title={typeof label === "string" ? label : undefined}
      >
        {label}
      </span>
      {value != null && (
        <span className="shrink-0 font-mono text-sm tabular-nums text-ink">
          {value}
        </span>
      )}
      {children}
    </Row>
  );
}

export interface FormTotalProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Defaults to "Total". */
  label?: React.ReactNode;
  /** The figure the rule is drawn under. Already formatted. */
  amount: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * The bottom line, ruled off the way it is on a printed form: the accountant's
 * double rule across the sheet, then the label and the figure on one baseline
 * at either edge.
 *
 * Sits at the foot of a run of `FormRow`s, inside a `CarbonForm`. The sum is
 * the caller's - money arithmetic does not belong in a layout component.
 */
export function FormTotal({
  label = "Total",
  amount,
  className,
  ...rest
}: FormTotalProps) {
  return (
    <div className={cn("px-4 pt-3 pb-3.5", className)} {...rest}>
      {/* Two hairlines out of one 3px box. */}
      <span
        aria-hidden="true"
        className="mb-2 block h-[3px] border-y border-ink"
      />
      <p className="flex items-baseline justify-between gap-3">
        <span className="text-annotation-caps text-ink">{label}</span>
        <span className="font-mono text-base font-medium tabular-nums text-ink">
          {amount}
        </span>
      </p>
    </div>
  );
}
